import { useState, useEffect } from 'react';
import { MobileSelect } from '@/components/ui';
import { updateCommunication } from '../services/clientCommunicationService';
import { TagSelector } from './TagSelector';
import {
  COMMUNICATION_TYPES,
  type CommunicationType,
  type CommunicationTag,
  type ClientCommunicationView,
} from '../types';

interface CommunicationEditModalProps {
  communication: ClientCommunicationView;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updated: ClientCommunicationView) => void;
}

export function CommunicationEditModal({
  communication,
  isOpen,
  onClose,
  onSave,
}: CommunicationEditModalProps) {
  const [communicationType, setCommunicationType] = useState<CommunicationType>(
    communication.communicationType
  );
  const [tags, setTags] = useState<CommunicationTag[]>(communication.tags);
  const [notes, setNotes] = useState(communication.notes || '');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when communication changes
  useEffect(() => {
    setCommunicationType(communication.communicationType);
    setTags(communication.tags);
    setNotes(communication.notes || '');
  }, [communication]);

  // Close on escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) onClose();
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose, isSaving]);

  if (!isOpen) return null;

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      await updateCommunication({
        id: communication.id,
        communicationType,
        tags,
        notes: notes || undefined,
      });

      // Create updated view object for parent
      const updated: ClientCommunicationView = {
        ...communication,
        communicationType,
        tags,
        notes: notes || null,
      };

      onSave(updated);
      onClose();
    } catch (err) {
      console.error('Error updating communication:', err);
      setError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges =
    communicationType !== communication.communicationType ||
    JSON.stringify(tags) !== JSON.stringify(communication.tags) ||
    (notes || '') !== (communication.notes || '');

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-secondary-200">
          <h3 className="text-lg font-semibold text-secondary-900">
            Edit Communication
          </h3>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="p-2 rounded-full hover:bg-secondary-100 transition-colors disabled:opacity-50"
          >
            <svg className="w-5 h-5 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* File Name (read-only) */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">
              File
            </label>
            <p className="text-secondary-900 truncate" title={communication.fileName}>
              {communication.fileName}
            </p>
          </div>

          {/* Communication Type */}
          <MobileSelect
            label="Communication Type"
            value={communicationType}
            onChange={(value) => setCommunicationType(value as CommunicationType)}
            options={COMMUNICATION_TYPES.map((t) => ({
              value: t.value,
              label: t.label,
            }))}
          />

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">
              Tags
            </label>
            <TagSelector
              value={tags}
              onChange={setTags}
              disabled={isSaving}
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1.5">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any context about this communication..."
              rows={3}
              disabled={isSaving}
              className="w-full px-3 py-2 border border-secondary-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-secondary-50 disabled:text-secondary-500"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-secondary-200">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="flex-1 px-4 py-2 border border-secondary-300 text-secondary-700 font-medium rounded-lg hover:bg-secondary-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className="flex-1 px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
